/**
 * <s-collection-form> — collection sort and filter controls.
 *
 * One navigation path for every change: build `<form action>?<non-empty fields>`
 * from the collection's own URL (never the current page URL) and load it like a
 * link, so Shopify renders sort, filter and pagination state server-side.
 *   - Sort select change  -> navigate
 *   - Form submit (Show results, Enter in a price field) -> navigate
 *   - sort_by is left out when it equals the collection default (data-default-sort).
 * Filter checkboxes do not navigate on their own; they apply on Show results.
 * Without JavaScript the same form submits natively as a GET to the same URL.
 */

/**
 * Pure URL builder (exported for testing). Drops empty values; keeps repeats.
 * Keeps any query the action already carries (e.g. Shopify's vendor listing
 * /collections/vendors?q=Fewmany) unless the form sends that field itself, and joins
 * with a single "?" so the result is always one valid query string.
 */
export function buildCollectionUrl(action, entries) {
  const base = new URL(action, 'https://collection.invalid');
  const list = [...entries];
  const formNames = new Set(list.map(([name]) => name));
  const params = new URLSearchParams();
  for (const [name, value] of base.searchParams) {
    if (!formNames.has(name)) params.append(name, value);
  }
  for (const [name, value] of list) {
    const text = String(value).trim();
    if (text) params.append(name, text);
  }
  const query = params.toString();
  return query ? `${base.pathname}?${query}` : base.pathname;
}

class CollectionForm extends HTMLElement {
  #ac;

  connectedCallback() {
    const form = this.querySelector('form');
    if (!(form instanceof HTMLFormElement)) return;
    this.#ac = new AbortController();
    const { signal } = this.#ac;

    const navigate = () => {
      const action = form.getAttribute('action') || window.location.pathname;
      // Keep a sort the shopper chose; omit sort_by when it equals the collection's own
      // default (data-default-sort), so applying filters never pins a default sort in the URL.
      const sortSelect = form.querySelector('select[name="sort_by"]');
      const defaultSort = sortSelect?.dataset.defaultSort || '';
      const entries = [...new FormData(form)].filter(
        ([name, value]) => !(name === 'sort_by' && defaultSort && String(value) === defaultSort),
      );
      const url = buildCollectionUrl(action, entries);
      this.setAttribute('aria-busy', 'true');
      window.location.assign(url);
    };

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      navigate();
    }, { signal });

    form.addEventListener('change', (event) => {
      const target = event.target;
      if (target instanceof HTMLSelectElement && target.name === 'sort_by') navigate();
    }, { signal });

    // Back/forward cache can restore a busy page; clear the state on return.
    window.addEventListener('pageshow', () => this.removeAttribute('aria-busy'), { signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }
}

if (!customElements.get('s-collection-form')) customElements.define('s-collection-form', CollectionForm);
